/**
 * City Domain Utilities
 * Helper functions for city operations
 */

import { getLocalizedValue } from '@spcc/core'
import type { CityListItem, CitiesFilter } from '../types'

/**
 * True if search matches code or any localized name / state strings.
 */
export function cityMatchesSearch(city: CityListItem, rawSearch: string): boolean {
  const q = rawSearch.trim().toLowerCase()
  if (!q) return true

  const parts: string[] = [city.code.toLowerCase()]
  parts.push(getLocalizedValue(city.name, 'en').toLowerCase())
  parts.push(getLocalizedValue(city.name, 'ar').toLowerCase())
  if (city.stateProvince) {
    parts.push(getLocalizedValue(city.stateProvince, 'en').toLowerCase())
    parts.push(getLocalizedValue(city.stateProvince, 'ar').toLowerCase())
  }
  return parts.some((s) => s.includes(q))
}

/**
 * Filter cities by status
 */
export function filterCitiesByStatus(
  cities: CityListItem[],
  isActive: boolean | 'all'
): CityListItem[] {
  if (isActive === 'all') return cities
  return cities.filter((city) => city.isActive === isActive)
}

/**
 * Filter cities by search term
 */
export function filterCitiesBySearch(
  cities: CityListItem[],
  search: string
): CityListItem[] {
  if (!search.trim()) return cities
  return cities.filter((city) => cityMatchesSearch(city, search))
}

/**
 * Filter cities by region
 */
export function filterCitiesByRegion(
  cities: CityListItem[],
  regionId: string | undefined
): CityListItem[] {
  if (!regionId) return cities
  return cities.filter((city) => city.regionId === regionId)
}

/**
 * Apply all filters to cities
 */
export function applyFilters(
  cities: CityListItem[],
  filter: CitiesFilter
): CityListItem[] {
  let result = [...cities]

  if (filter.isActive !== undefined && filter.isActive !== 'all') {
    result = filterCitiesByStatus(result, filter.isActive)
  }

  if (filter.search) {
    result = filterCitiesBySearch(result, filter.search)
  }

  if (filter.regionId) {
    result = filterCitiesByRegion(result, filter.regionId)
  }

  return result
}

/**
 * Sort cities by localized display name
 */
export function sortCitiesByName(
  cities: CityListItem[],
  displayLang: string = 'en'
): CityListItem[] {
  const collator = displayLang.startsWith('ar') ? 'ar' : 'en'
  return [...cities].sort((a, b) =>
    getLocalizedValue(a.name, displayLang).localeCompare(
      getLocalizedValue(b.name, displayLang),
      collator
    )
  )
}

/**
 * Generate a unique city code
 */
export function generateCityCode(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 4).toUpperCase()
  return `CITY-${timestamp}-${random}`
}
